'use client';
import React, { useEffect, useState } from 'react';
import VideoLoadModal from '@/components/pages/features/VideoLoadModal';
import { useSession } from 'next-auth/react';
import { FaLock } from 'react-icons/fa6';
import { BiSolidVideoRecording } from 'react-icons/bi';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import {MetaEntity} from "@/types/meta";
import {ChapterPartEntity, CourseContentLockEnum, CourseEntity} from "@/types/course";
import ElevatedButton from "@/components/ui/buttons/global/ElevatedButton";
import {CustomDialogComponent} from "@/components/ui/dialog/CustomDialogComponent";
import DashboardExpiredLogin from "@/app/(dashboard)/dashboard/_components/common/DashboardExpiredLogin";

const CourseChaptersSection = ({ meta, showModalLoginAgainDialog, canAccess }: {
  meta: MetaEntity;
  canAccess?: boolean;
  showModalLoginAgainDialog?: boolean
}) => {

  const queryParameters = new URLSearchParams(useSearchParams().toString());
  queryParameters.set('callback', meta.slug);
  const linkToSignIn = `/auth/signin${queryParameters.size ? '?' + queryParameters.toString() : ''}`;

  const course = meta.data as CourseEntity;

  const isFa = course.lang === 'fa';

  const [showVideoModal, setShowVideoModal] = useState<ChapterPartEntity>();
  const [openedIndex, setOpenedIndex] = useState<number>();
  const { data: session } = useSession();
  const [showLoginAgainDialog, setShowLoginAgainDialog] = useState<boolean>();

  useEffect(() => {
    if (showModalLoginAgainDialog) {
      setShowLoginAgainDialog(true);
    }
  }, [showModalLoginAgainDialog]);

  const loadUrl = (part: ChapterPartEntity | undefined) => {
    if(part?.lock === CourseContentLockEnum.purchase){
      return `${part.url}/${session?.user?.id}`;
    }

    return part?.url;
  };

  return (
    <>
      {course.chapters?.map((chapter, index) => (
        <div key={`chapter-${index}`}
             className={'bg-stone-200 dark:bg-stone-800 my-2 rounded-xl transition-all duration-300'}>
          <button
            className="flex w-full items-center justify-between text-left font-semibold p-5"
            onClick={(e) => {
              e.preventDefault();
              setOpenedIndex(prevState => {
                if (prevState === index) {
                  return undefined;
                } else {
                  return index;
                }
              });
            }}>

            <h6 className={'font-semibold'}>{chapter.title}</h6>
            <div>

              <svg className="fill-indigo-500 shrink-0" width="16" height="16"
                   xmlns="http://www.w3.org/2000/svg">
                <rect y="7" width="16" height="2" rx="1"
                      className={`ttransform origin-center transition duration-200 ease-out ${openedIndex === index && '!rotate-180'}`} />
                <rect y="7" width="16" height="2" rx="1"
                      className={`transform origin-center rotate-90 transition duration-200 ease-out ${openedIndex === index && '!rotate-180'}`} />
              </svg>

            </div>
          </button>

          <div
            className={`grid text-sm transition-all duration-300 ease-in-out ${openedIndex === index ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
          >

            <div className={`overflow-hidden transition-all duration-300 ${openedIndex === index && 'm-2 sm:m-3'} space-y-2`}>
              {chapter.parts.map((part, partInx) => (
                <div
                  className={`flex flex-wrap flex-col sm:flex-row p-3 gap-2 rounded-xl bg-stone-100 dark:bg-stone-900 items-start sm:items-center`}
                  key={`ch-${index}-part-${partInx}`}
                >
                  <div className={'flex-1'}>
                    <p className={'font-bold'}>{part.title}</p>
                    {part.des && (<p className={'text-sm'}>{part.des}</p>)}
                  </div>

                  <div>

                    {
                      !part.url ? (
                        <BiSolidVideoRecording className={'mx-2'} />
                      ) : (
                        part.lock === CourseContentLockEnum.free ? (
                          <ElevatedButton
                            width={''}
                            textSize={'text-sm'}
                            onClick={() => setShowVideoModal(part)}>
                            {isFa ? 'نمایش رایگان' : 'Watch free'}
                          </ElevatedButton>
                        ) : part.lock === CourseContentLockEnum.registerRequired ? (
                          // checking registered
                          session?.user?.name ? (
                            <ElevatedButton
                              width={''}
                              textSize={'text-sm'}
                              onClick={() => setShowVideoModal(part)}>
                              {isFa ? 'نمایش' : 'Preview'}
                            </ElevatedButton>
                          ) : (
                            <Link
                              className={'underline hover:underline-offset-8 underline-offset-2 hover:decoration-blue-500 transition-all duration-300 ms-2'}
                              href={linkToSignIn}>
                              <p
                                className={'text-s'}>{isFa ? 'برای نمایش رایگان وارد حساب شوید' : 'Log in to watch for free'}</p>
                            </Link>
                          )
                        ) : (
                          canAccess ? (
                            <ElevatedButton
                              width={''}
                              textSize={'text-sm'}
                              onClick={() => setShowVideoModal(part)}>
                              {isFa ? 'نمایش' : 'View'}
                            </ElevatedButton>
                          ) : (
                            <FaLock className={'mx-2'} />
                          )
                        )
                      )
                    }

                  </div>
                </div>
              ))}

            </div>
          </div>
        </div>
      ))}

      {showLoginAgainDialog && <CustomDialogComponent>
        <div className={'p-3'}>
          <DashboardExpiredLogin disableFullScreen />
        </div>
      </CustomDialogComponent>}

      {showVideoModal &&
        <VideoLoadModal
          url={loadUrl(showVideoModal)!}
          onCloseModal={() => {
            setShowVideoModal(undefined);
          }} />
      }

    </>
  );
};

export default CourseChaptersSection;
